import React, { useEffect, useMemo, useState } from 'react'

import { Col, Modal, Row } from 'react-bootstrap'
import { Button, Paragraph } from 'src/components'
import { faCheck, faWindowClose } from '@fortawesome/free-solid-svg-icons'

import { ItemTaxOperation } from 'src/components/organims/itemsTaxOperations/ItemTaxOperation'
import { toMoney } from 'src/utils/utilities'
import {
  calculateSubtotals,
  getTaxByItems,
  getTaxRetainByItems,
} from 'src/components/organims/itemsTaxOperations/TaxOperationFunctions'

import { TaxOperationTypeEnum } from 'src/components/organims/itemsTaxOperations/TaxOperationTypeEnum'
import { useDispatch, useSelector } from 'react-redux'
import { getAllTaxes } from 'src/actions/taxes.actions'
import { selectAllTaxesRetention } from 'src/selectors/taxes.selector'
import { findTypeByValue } from 'src/content/Taxes/enums/TaxTypeEnum'

interface ItemsTaxOperationsProps {
  show: boolean
  type: TaxOperationTypeEnum
  items: IItemTaxOperation[]
  onClose: () => void
  onSave: (items: IItemTaxOperationResponse[], updated: IItemTaxOperation[]) => void
  taxIncluded?: boolean
  taxDetailed?: boolean
}

export const TaxOperations = ({
  show,
  items,
  onClose,
  onSave,
  taxIncluded = false,
  taxDetailed = false,
  type,
}: ItemsTaxOperationsProps) => {
  const dispatch = useDispatch()

  const taxes: Tax[] = useSelector(selectAllTaxesRetention)

  const taxConfigurations: ITaxConfigurations = { taxIncluded, taxDetailed }

  const [itemList, setItemList] = useState<IItemTaxOperation[]>([])

  useEffect(() => {
    if (!show) return
    setItemList(items)
    if (type === TaxOperationTypeEnum.RETENTION_ISR) dispatch(getAllTaxes())
  }, [show])

  const onChangeItem = (item: IItemTaxOperation) => {
    const updatedItems = [...itemList]
    const index = updatedItems.findIndex(
      i => i.metadata.entityId === item.metadata.entityId,
    )
    if (index > -1) updatedItems[index] = item

    setItemList(updatedItems)
  }

  const updateTaxAll = (exempt: boolean) => {
    const updatedItems = itemList.map(item => ({
      ...item,
      taxException: exempt,
    }))
    setItemList(updatedItems)
  }

  const onChange = () => {
    let response: IItemTaxOperationResponse[]
    if (type === TaxOperationTypeEnum.EXEMPT)
      response = itemList
        .filter(item => item.taxException)
        .map(item => ({
          id: item.metadata.entityId,
          taxException: item.taxException,
          amount: getTaxByItems([item], taxConfigurations),
        }))
    else if (type === TaxOperationTypeEnum.RETENTION_ISR)
      response = itemList
        .filter(item => item.taxRetention?.id)
        .map(item => ({
          id: item.metadata.entityId,
          taxException: false,
          amount: getTaxRetainByItems([item], taxConfigurations),
          retentionId: item.taxRetention.id,
        }))
    onSave(response, itemList)
  }

  const subtotals = useMemo(() => {
    if (!show) return []
    return calculateSubtotals(itemList, taxConfigurations)
  }, [show, itemList, taxIncluded, taxDetailed])

  const renderSubtotals = () => {
    return (
      <Row className={'mt-3'}>
        <Col xl={12}>
          <Paragraph bold>Totales</Paragraph>
        </Col>

        {subtotals?.map(({ label, amount }, key) => (
          <Col key={key} xl={12} className="d-flex justify-content-between">
            <Paragraph>{label}</Paragraph>
            <Paragraph>{toMoney(amount)}</Paragraph>
          </Col>
        ))}
      </Row>
    )
  }

  const renderExemptActions = (
    <>
      <Button color={'accent'} onClick={() => updateTaxAll(true)}>
        Exentar todo
      </Button>
      <Button color={'primary'} onClick={() => updateTaxAll(false)}>
        Quitar exentos
      </Button>
    </>
  )

  return (
    <div>
      <Modal show={show} centered size="xl">
        <Modal.Header>
          <Modal.Title>Implementación de {findTypeByValue(type)?.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body className={'custom-modal-body'}>
          <Row>
            <Col xl={12}>
              <Row className={'container-buttons'}>
                {type === TaxOperationTypeEnum.EXEMPT && renderExemptActions}
              </Row>
            </Col>
            <Col xl={12}>
              <Row>
                <Col xl={12} className={'d-flex'}>
                  <div style={{ width: '20%' }}>
                    <Paragraph bold>
                      {type === TaxOperationTypeEnum.EXEMPT ? 'Exento' : 'Retención'}{' '}
                    </Paragraph>
                  </div>
                  <div style={{ width: '40%' }}>
                    <Paragraph bold>Item</Paragraph>
                  </div>
                  <div style={{ width: '40%' }}>
                    <Paragraph bold>Valores</Paragraph>
                  </div>
                </Col>
                {itemList.map((item, index) => (
                  <Col key={index} xl={12}>
                    <ItemTaxOperation
                      taxes={taxes}
                      item={item}
                      type={type}
                      onChange={onChangeItem}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
            <Col xl={12}>{renderSubtotals()}</Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row className={'container-buttons'}>
            <Button icon={faWindowClose} color={'secondary'} onClick={onClose}>
              Cerrar
            </Button>
            <Button icon={faCheck} onClick={() => onChange()}>
              Continuar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
